import React, { useEffect, useState } from 'react';
import SearchableMultiSelect from './SearchableMultiSelect';
import api from '../../utils/api';

// Meta pixel events (standard + custom) as report metrics. Structurally the twin of
// MetaCustomConversions: fetched per account/pixel, persisted as
// [{id,name,kind,actionType}], each selection resolved server-side out of the insights
// `actions` AND `conversions` arrays merged — see pixelEventActionType and
// mergeActionPools in server/services/cognitive-flows/metaAdsReportService.js.
//
// The split matters here because it drives what this picker can offer: `actions` only
// ever carries an unnamed rollup of every custom event, so named custom events come
// exclusively from `conversions`.
//
// Shared by the two Meta editors that must stay in sync:
// components/nodes/AdsReadConfig.js and pages/CognitiveFlowsDataSourcesPage.jsx.

// Entity-snapshot reports emit fixed columns and ignore metric selection.
// Mirrors META_FIXED_COLUMN_REPORTS in server metaAdsReportService.js.
const FIXED_COLUMN_REPORTS = new Set(['ad_enhancements', 'adset_advantage', 'creative', 'adset_targeting']);

// Mirrors ALL_CUSTOM_EVENTS_ID in server metaAdsReportService.js — the synthetic id for
// Meta's aggregate custom-event bucket. Deliberately not a plausible event name so it
// cannot collide with a real one.
const ALL_CUSTOM_EVENTS_ID = '__all_custom_events__';

/** True when pixel events apply to this report (Meta insights reports only). */
export function supportsPixelEvents(platform, reportType) {
  return platform === 'meta' && !!reportType && !FIXED_COLUMN_REPORTS.has(reportType);
}

/**
 * The account's pixel events, refetched when the account or pixel changes.
 * Standard events come back even without a pixel, so the picker is usable before one
 * is chosen — only the custom events need it.
 */
export function useMetaPixelEvents(accountId, pixelId, { enabled = true } = {}) {
  const [list, setList] = useState([]);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !accountId) { setList([]); setWarning(null); setError(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get('/meta-ads/pixel-events', { params: { accountId, pixelId: pixelId || '' } })
      .then(({ data }) => {
        if (cancelled) return;
        // `warning` means discovery partially failed: the standard events are still
        // usable, so this is not the `error` path.
        setList(data?.events || []);
        setWarning(data?.warning || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setList([]);
        setWarning(null);
        setError(err?.response?.data?.error || 'Could not load pixel events');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, pixelId, enabled]);

  return { pixelEvents: list, warning, loading, error };
}

/**
 * Searchable multi-select of standard + custom pixel events.
 * `selected` / `onChange` carry [{ id, name, kind }] — the name is persisted so a saved
 * source keeps a stable column header, and `kind` is what tells the server whether to
 * build a standard or custom action_type.
 */
export default function PixelEventsSelector({
  accountId,
  pixelId,
  selected = [],
  onChange,
  disabled = false,
  styles,
  includeCost = false,
  onIncludeCostChange,
  includeValue = false,
  onIncludeValueChange,
}) {
  const { pixelEvents, warning, loading, error } = useMetaPixelEvents(accountId, pixelId);

  // Cost and value are siblings: each adds one companion column per selection.
  const toggle = (checked, onToggle, text) => (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      fontSize: '12px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
    }}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onToggle(e.target.checked)}
        disabled={disabled}
        style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
      />
      <span>{text}</span>
    </label>
  );
  const costToggle = onIncludeCostChange ? toggle(includeCost, onIncludeCostChange, 'Include cost per event') : null;
  const valueToggle = onIncludeValueChange ? toggle(includeValue, onIncludeValueChange, 'Include event value') : null;
  const toggleRow = (costToggle || valueToggle) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginTop: '6px' }}>
      {costToggle}
      {valueToggle}
    </div>
  ) : null;

  if (!accountId) return <span style={styles.hint}>Select an ad account to load its pixel events.</span>;
  if (loading) return <span style={styles.hint}>Loading pixel events…</span>;
  if (error) return <span style={styles.hint}>{error}</span>;

  const known = new Set(pixelEvents.map((e) => String(e.id)));
  const stranded = selected.filter((e) => !known.has(String(e.id)));

  const options = [
    ...pixelEvents.map((e) => {
      if (e.id === ALL_CUSTOM_EVENTS_ID) {
        return {
          value: String(e.id),
          label: e.name,
          // Points at the per-event entries rather than away from them. This used to
          // claim Meta couldn't break custom events out by name and sent people off to
          // create a Custom Conversion as a workaround — both wrong: the named events
          // are right here in the same list, read from the insights `conversions` array.
          hint: 'Every custom event on this account added together. To report on one event, pick it by name from this list instead.',
        };
      }
      return {
        value: String(e.id),
        label: e.kind === 'custom' ? `${e.name} (custom)` : e.name,
        hint: e.kind === 'custom' ? 'Custom pixel event' : 'Standard pixel event',
      };
    }),
    // A saved selection missing from the list means one of two very different things,
    // and conflating them cost people their columns: when discovery FAILED we know
    // nothing about this event, so it must not be labelled as removable.
    ...stranded.map((e) => (warning
      ? {
        value: String(e.id),
        label: e.name,
        hint: "Kept from your saved selection — this account's events could not be read just now.",
      }
      : {
        value: String(e.id),
        label: `${e.name} (not in this account)`,
        hint: 'No longer reported by this account — untick to remove the column.',
      })),
  ];

  const metaFor = (id) =>
    pixelEvents.find((e) => String(e.id) === id)
    // Prefer the saved selection over re-deriving: it already carries the actionType
    // Meta gave us, and guessing one would mint a permanently-zero column.
    ?? selected.find((e) => String(e.id) === id)
    ?? { id, name: id, kind: 'standard' };

  return (
    <>
      {warning && (
        <span style={{ ...styles.hint, color: '#b45309', display: 'block', marginBottom: '6px' }}>
          {warning}
        </span>
      )}
      <SearchableMultiSelect
        value={selected.map((e) => String(e.id))}
        options={options}
        onChange={(ids) => onChange(ids.map((id) => {
          const m = metaFor(id);
          return {
            id,
            name: m.name || id,
            kind: m.kind || 'standard',
            // Carry Meta's own action_type through to the saved source. Without it the
            // server falls back to constructing one, which is the guesswork this
            // discovery exists to remove.
            ...(m.actionType ? { actionType: m.actionType } : {}),
          };
        }))}
        styles={styles}
        disabled={disabled}
        placeholder="— Select pixel events —"
        emptyText="No matching events"
        summarize={(n) => `${n} events selected`}
      />
      {toggleRow}
    </>
  );
}
